import { liveQuery, Subscription } from 'dexie'
import { defineStore } from 'pinia'

import { AppNotifications } from '@/shared/apis/bgnotifications'
import {
  GlobalDatabase,
  PopupDatabase,
  switchPopupDatabase,
} from '@/popup/storage/Database'
import { useNotifications } from '@/popup/compositions/useNotifications'
import { useApprovalsStore } from '@/popup/store/ApprovalsStore'
import { checkForLastPopupState } from '@/popup/utils/checkForLastPopupState'
import { usePreferencesStore } from '@/popup/store/PreferencesStore'
import { fetchLatestProfile } from '@/popup/utils/profiles'
import { useGlobals } from '@/popup/compositions/useGlobals'
import { Restriction } from '@/shared/models/Restriction'
import { dexieSubscriptionCallback } from '@/popup/utils/dexieSubscriptionCallback'
import {
  getNetworkNativeTokenSymbol,
  getPreviousNetwork,
} from '@/shared/NetworkUtils'
import {
  GetProfileData,
  InternalAPI,
  InternalMethods,
} from '@/shared/apis/messages'
import { assertNotUndefined } from '@/shared/validators'
import {
  FrontendProfile,
  DropdownProfile,
  ProfileList,
  LastPopupState,
  UpAddress,
  PopupProfile,
  EoaAddress,
  BackgroundProfile,
} from '@/shared/models/Profile'
import { PopupNetworkInfo } from '@/shared/models/Network'
import { Sign } from '@/shared/models/Sign'
import { Transaction } from '@/shared/models/Transaction'
import { AuthenticatedApp } from '@/shared/models/AuthenticatedApp'
import { useAppPermissionStore } from './AppPermissionStore'
import { getCurrentTabUrl } from '@/popup/utils/getCurrentTabUrl'
import { logInfo } from '@/shared/logger'
import { SomethingWentWrongError } from '@/shared/models/SomethingWentWrongError'
import { ControllerDTO } from '@/business-logic/profile'

const FETCH_COUNT = 10
const PROFILE_NOT_FOUND = 'Profile not found'

export type AppState = {
  state: {
    isCreated: boolean
    isUnlocked: boolean
    appId: string | undefined
    showNetworkModal?: boolean
    hasDisplayedNetworkSwitchInfo?: boolean
    networkModalName?: string
  }
  profiles: ProfileList
  subscriptions: Subscription[]
  globalSubscriptions: Subscription[]
  selectedProfile: UpAddress | undefined
  /**
   * The controller address of the UP that is currently selected.
   */
  upExtensionControllerAddress: EoaAddress | undefined
  lastPopupState?: LastPopupState
  authenticatedApps: AuthenticatedApp[]
  authenticatedAppsByOrigin: AuthenticatedApp[]
  transactions: Transaction[]
  somethingWentWrongErrors: SomethingWentWrongError[]
  fetchCount: number
  signs: Sign[]
  network: PopupNetworkInfo
}

export const useAppStore = defineStore('app', {
  state: () =>
    ({
      state: {
        isCreated: false,
        isUnlocked: false,
        appId: undefined,
        hasDisplayedNetworkSwitchInfo: undefined,
      },
      network: {} as PopupNetworkInfo,
      profiles: {},
      subscriptions: [],
      globalSubscriptions: [],
      selectedProfile: undefined,
      upExtensionControllerAddress: undefined,
      lastPopupState: undefined,
      authenticatedApps: [],
      authenticatedAppsByOrigin: [],
      transactions: [],
      fetchCount: FETCH_COUNT,
      signs: [],
      somethingWentWrongErrors: [],
    }) as AppState,

  getters: {
    currentProfile: state => {
      logInfo('AppStore.currentProfile', state.selectedProfile, state.profiles)
      const profile = state.profiles[state.selectedProfile || '']

      if (!profile) {
        return { address: '0x' } as FrontendProfile
      }
      if (profile.temporary && Object.keys(profile.temporary).length > 0) {
        return profile.temporary as FrontendProfile
      }
      if (profile.saved && Object.keys(profile.saved).length > 0) {
        return profile.saved as FrontendProfile
      }
      return { address: state.selectedProfile } as FrontendProfile
    },
    dropdownAddresses: state => {
      const dropdownAddresses: DropdownProfile[] = []
      for (const item in state.profiles) {
        const profile =
          state.profiles[item].temporary || state.profiles[item].saved
        profile &&
          dropdownAddresses.push({
            address: profile.address,
            name: profile.name,
            profilePicture: profile.profilePicture,
            verification: profile.verification,
            createdAt: state.profiles[item].createdAt,
          })
      }
      return dropdownAddresses
    },
    pendingApprovalsCount: () => {
      const approvalsStore = useApprovalsStore()
      return approvalsStore.profileApprovals.length
    },
  },

  actions: {
    async coreInit(data: InternalAPI['get-core-state']['response']) {
      logInfo('AppStore.coreInit', data)
      this.state.isUnlocked = data.isUnlocked
      this.state.isCreated = data.isCreated
      this.state.appId = data.appId
      this.network = data.network
      const nativeTokenSymbol = await getNetworkNativeTokenSymbol(
        data.network.chainId
      )
      await usePreferencesStore().init(data.locale, nativeTokenSymbol)
      await this.initProfiles()
    },
    async initDbSubscriptions(
      profileAddress: string,
      lastPopupState?: LastPopupState,
      origin?: string
    ) {
      let promiseResolve: any
      let loadedTables = 0
      const noOfTablesToLoad = 5

      const promise = new Promise(resolve => {
        promiseResolve = resolve
      })

      const checkIfResolvePromise = () => {
        loadedTables++
        if (loadedTables === noOfTablesToLoad) {
          promiseResolve()
        }
      }

      this.subscriptions.push(
        dexieSubscriptionCallback<BackgroundProfile | undefined>(
          async () => await PopupDatabase.getProfile(profileAddress),
          entry => {
            checkIfResolvePromise()
            this.upExtensionControllerAddress = entry?.controllerAddress
          }
        ),
        dexieSubscriptionCallback<AuthenticatedApp[]>(
          async () => await PopupDatabase.getAuthenticatedApps(profileAddress),
          entry => {
            checkIfResolvePromise()
            this.authenticatedApps = entry
          }
        ),
        dexieSubscriptionCallback<AuthenticatedApp[]>(
          async () =>
            origin
              ? await PopupDatabase.getAllAuthenticatedAppsByOrigin(origin)
              : [],
          entry => {
            checkIfResolvePromise()
            this.authenticatedAppsByOrigin = entry
          }
        ),
        dexieSubscriptionCallback<Sign[]>(
          async () => await PopupDatabase.getSigns(profileAddress),
          entry => {
            checkIfResolvePromise()
            this.signs = entry
          }
        ),
        dexieSubscriptionCallback<Restriction[]>(
          async () => await PopupDatabase.getRestrictions(profileAddress),
          entry => {
            checkIfResolvePromise()
            useAppPermissionStore().init(entry, lastPopupState)
          }
        ),
        dexieSubscriptionCallback<Transaction[]>(
          async () => await PopupDatabase.getTransactions(profileAddress),
          entry => {
            checkIfResolvePromise()
            this.transactions = entry
          }
        )
      )

      return promise
    },
    async init(
      data: InternalAPI[InternalMethods.GET_STATE]['response'],
      lastPopupState: LastPopupState | undefined,
      isPopup: boolean
    ) {
      logInfo('AppStore.init', data)
      await this.swapProfile(data, lastPopupState, isPopup)
    },
    _loadProfiles(profiles: PopupProfile[]) {
      const profiles_: ProfileList = {}
      for (const profile of profiles) {
        profiles_[profile.address] = profile
      }
      this.profiles = profiles_
    },
    async initProfiles() {
      const popupProfiles = await PopupDatabase.getPopupProfiles()
      logInfo('AppStore.initProfiles', popupProfiles)
      this._loadProfiles(popupProfiles)

      this.globalSubscriptions.push(
        liveQuery(async () => {
          return await PopupDatabase.getPopupProfiles()
        }).subscribe(profiles => {
          this._loadProfiles(profiles)
        })
      )
    },
    async initLastPopupState(lastPopupState?: LastPopupState) {
      this.lastPopupState = lastPopupState

      const lastPopupStateQuery = liveQuery(async () => {
        return await PopupDatabase.getLastPopupState(this.selectedProfile)
      })

      this.subscriptions.push(
        lastPopupStateQuery.subscribe(lastPopupState => {
          this.lastPopupState = lastPopupState
        })
      )
    },
    async profileUpdated(profileAddress: string) {
      if (this.selectedProfile) {
        const latestProfile = await fetchLatestProfile(this.currentProfile)
        const oldProfile = this.profiles[this.selectedProfile]

        if (latestProfile) {
          await this.savePopupProfile({
            ...oldProfile.saved,
            ...latestProfile,
          })
        }
        await this.removeTemporaryProfile(profileAddress)
        const router = useGlobals().router
        if (
          router.currentRoute.value.path.startsWith('/add-account') ||
          router.currentRoute.value.path.startsWith('/add-base-account')
        ) {
          router.push('/')
        }
      }
    },
    async profileImported(profileAddress: string) {
      const profile = await PopupDatabase.getProfile(profileAddress)
      if (profile) {
        await useGlobals().sendMessage({
          method: InternalMethods.PROFILE_IMPORTED,
          payload: {
            account: profileAddress,
          },
        })

        await PopupDatabase.put('profiles', {
          ...profile,
          importStatus: 'imported',
          importOriginDapp: undefined,
        })
        await this.removeTemporaryProfile(profile.address)
        const latestProfile = await fetchLatestProfile(profile)
        assertNotUndefined(latestProfile, PROFILE_NOT_FOUND)
        await this.savePopupProfile({
          ...latestProfile,
          importStatus: 'imported',
        })
      }
    },
    async initNotifications(address: string) {
      const { showToast, clearToasts } = useNotifications()
      clearToasts()

      const notificationsQuery = liveQuery(() => {
        return PopupDatabase.getNotifications(address)
      })
      this.subscriptions.push(
        notificationsQuery.subscribe(async notifications => {
          for (const notification of notifications) {
            switch (notification.method) {
              case AppNotifications.PROFILE_UPDATE_ERROR: {
                await this.removeTemporaryProfile(address)
                break
              }
              case AppNotifications.PROFILE_UPDATED: {
                if (!notification.profileAddress) {
                  continue
                }
                await this.profileUpdated(notification.profileAddress)
                break
              }
              case AppNotifications.PROFILE_IMPORTED: {
                if (!notification.profileAddress) {
                  continue
                }
                await this.profileImported(notification.profileAddress)
                break
              }
            }
          }

          if (notifications.length > 0) {
            showToast(notifications[0])
          } else {
            clearToasts()
          }
        })
      )
    },
    async switchNetworkDatabase(networkId: string) {
      for (const subscription of this.globalSubscriptions) {
        subscription.unsubscribe()
      }
      this.globalSubscriptions = []
      await switchPopupDatabase(networkId)
    },
    async switchToPreviousNetwork() {
      const previousNetworkId = await getPreviousNetwork()
      await this.switchToNetwork(previousNetworkId)
    },
    async setHasDisplayedNetworkSwitchInfo() {
      this.state.hasDisplayedNetworkSwitchInfo = true
    },
    async switchToNetwork(networkId: string, approvalId?: string) {
      this.state.showNetworkModal = true
      const databaseNetwork = await GlobalDatabase.getNetwork(networkId)

      this.state.networkModalName = databaseNetwork?.name
      try {
        await useGlobals().sendMessage({
          method: InternalMethods.SWITCH_NETWORK,
          payload: { networkId, approvalId },
        })
        await this.switchNetworkDatabase(networkId)

        const { sendMessage } = useGlobals()
        const coreState = await sendMessage({
          method: InternalMethods.GET_CORE_STATE,
          payload: undefined,
        })
        const data = await sendMessage({
          method: InternalMethods.GET_STATE,
          payload: undefined,
        })
        await this.coreInit(coreState)
        await this.init(data, undefined, false)
        return data.selectedProfile
      } finally {
        await new Promise(resolve => setTimeout(resolve, 1000))
        this.state.showNetworkModal = false
      }
    },
    async swapProfile(
      data: GetProfileData,
      lastPopupState?: LastPopupState,
      isPopup?: boolean
    ) {
      if (data.selectedNetwork) {
        this.network = data.selectedNetwork
      }
      for (const subscription of this.subscriptions) {
        subscription.unsubscribe()
      }
      this.subscriptions = [
        dexieSubscriptionCallback<SomethingWentWrongError[]>(
          async () => await PopupDatabase.getUnreadSomethingWentWrongErrors(),
          entry => {
            this.somethingWentWrongErrors = entry
          }
        ),
      ]

      const address = data.selectedProfile
      if (!address) {
        return
      }
      this.selectedProfile = address
      if (!isPopup) {
        await this.initNotifications(address)
        await this.initLastPopupState(lastPopupState)
      }
      useApprovalsStore().setProfileApprovals(data.approvals)
      const currentTabUrl = await getCurrentTabUrl()
      const url = currentTabUrl ? new URL(currentTabUrl) : undefined
      await this.initDbSubscriptions(
        this.selectedProfile,
        lastPopupState,
        url?.origin
      )
    },
    async handleUserChange(data: InternalAPI['switch-profile']['response']) {
      if (data.selectedProfile) {
        const lastPopupState = await checkForLastPopupState(
          data.selectedProfile
        )
        await this.swapProfile(data, lastPopupState)
      }
    },
    async handleCreatedAccount({ appId }: { appId: string }) {
      this.state.isCreated = true
      this.state.isUnlocked = true
      this.state.appId = appId
    },
    async handleRestoredAccount({
      profile,
      isLastProfile,
    }: {
      profile: FrontendProfile
      isLastProfile?: boolean
    }) {
      this.state.isUnlocked = true
      await PopupDatabase.insertPopupProfile({
        address: profile.address,
        temporary: profile,
        saved: undefined,
        isOwner: true,
        createdAt: Date.now(),
      })
      if (isLastProfile) {
        await this.swapProfile({
          selectedProfile: profile.address,
          approvals: [],
        })
      }
    },
    async removeTemporaryProfile(address: string) {
      await PopupDatabase.updatePopupProfile(address, 'temporary')
    },
    async savePopupProfile(profile: FrontendProfile) {
      await PopupDatabase.updatePopupProfile(profile.address, 'saved', profile)
    },
    async setTemporaryProfile(profile: FrontendProfile) {
      await PopupDatabase.updatePopupProfile(
        profile.address,
        'temporary',
        profile
      )
    },
    setIsUnlocked(value: boolean) {
      this.state.isUnlocked = value
    },
    async getAllUniversalProfilesDeviceControllers() {
      const eachUniversalProfileDeviceControllers: Record<
        UpAddress,
        ControllerDTO
      > = {}
      for await (const profile of this.dropdownAddresses) {
        const controller = await useGlobals().sendMessage({
          method: InternalMethods.GET_UP_BROWSER_EXTENSION_CONTROLLER,
          payload: {
            profileAddress: profile.address,
          },
        })
        if (controller) {
          eachUniversalProfileDeviceControllers[profile.address] = controller
        }
      }
      return eachUniversalProfileDeviceControllers
    },
  },
})
